import ParticipantManager from './participant-manager';
import FormService from './form-service';
import TemplateService from './template-service';

const TEMPLATE = `
<div class="fc-flexbox-animation-card">
    <div class="event-flex-container">
        <div class="event-time-info" style="display:none;">
            \${time}
        </div>
        <div class="event-main-info">
            <div class="event-categories">
                \${categories}
            </div>
            <div class="event-title">\${title}</div>
            <div class="event-actions">
                \${showInscriptionBtn}
            </div>
        </div>
        <div class="event-action-area">
            <div class="chevron-icon">
                <svg viewBox="0 0 24 24">
                    <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z"/>
                </svg>
            </div>
        </div>
    </div>
</div>`;

class AnimationCard {
    constructor(data) {
        this.data = data;
        this.element = null;
        this.participantManager = null;
        
        this._categoryIconsCache = {};
        if (data.extendedProps && data.extendedProps.categories_icons) {
            Object.keys(data.extendedProps.categories_icons).forEach(cat => {
                this._categoryIconsCache[cat] = data.extendedProps.categories_icons[cat];
            });
        }
        
        this.init();
    }

    init() {
        try {
            // Créer l'élément à partir du template
            const html = this.render();
            if (!html) {
                return null;
            }

            const template = document.createElement('template');
            template.innerHTML = html.trim();
            
            // Vérifier que le template a généré un élément valide
            if (!template.content.firstChild) {
                return null;
            }

            this.element = template.content.firstChild;
            this.ensureCategoryStyles();
            
            // S'assurer que l'élément a un ID basé sur l'ID de l'événement
            if (this.data.id) {
                this.element.setAttribute('data-event-id', this.data.id);
            }

            // Attacher les gestionnaires d'événements
            this.attachEventHandlers();
            
            return this.element;
        } catch (error) {
            return null;
        }
    }

    render() {
        try {
            if (!this.data) {
                return null;
            }

            // Formater les données pour le template
            let categories = Array.isArray(this.data.categories) 
                ? this.data.categories 
                : (this.data.extendedProps?.categories || []);
                
            // Transformer les catégories si elles sont des chaînes
            if (categories.length > 0 && typeof categories[0] === 'string') {
                categories = categories.map(cat => {
                    // Récupérer la couleur depuis les données des catégories
                    let color = null;
                    let icon = null;
                    
                    // Vérifier si on a des couleurs définies dans les propriétés étendues
                    if (this.data.extendedProps && this.data.extendedProps.categories_colors) {
                        color = this.data.extendedProps.categories_colors[cat];
                    }
                    
                    // Vérifier si on a des icônes définies dans les propriétés étendues
                    if (this.data.extendedProps && this.data.extendedProps.categories_icons) {
                        icon = this.data.extendedProps.categories_icons[cat];
                    }
                    
                    return {
                        name: cat,
                        // N'utiliser getCategoryColor que si aucune couleur n'est définie
                        color: color || this.getCategoryColor(cat),
                        // Ajouter l'icône si elle existe
                        icon: icon || null
                    };
                });
            }
            
            const templateData = {
                time: this.data.time || '',
                title: this.data.title || '',
                lieu_depart: this.data.lieu_depart || this.data.extendedProps?.lieu_depart || '',
                places_disponibles: this.data.places_disponibles || this.data.extendedProps?.places_disponibles || '0',
                id: this.data.id || '',
                categories: categories,
                disable_native_inscription: this.data.disable_native_inscription === true || this.data.extendedProps?.disable_native_inscription === true,
                external_inscription_url: this.data.external_inscription_url || this.data.extendedProps?.external_inscription_url || '',
                external_inscription_label: this.data.external_inscription_label || this.data.extendedProps?.external_inscription_label || ''
            };

            // Vérifier les données essentielles
            if (!templateData.id) {
                return null;
            }

            // Générer le HTML
            const rendered = this.fillTemplate(templateData);
            if (!rendered) {
                return null;
            }

            return rendered;
        } catch (error) {
            return null;
        }
    }

    fillTemplate(data) {
        if (!data) {
            return null;
        }
        
        try {
            // Convertir places_disponibles en nombre entier
            const placesDisponibles = parseInt(data.places_disponibles || '0', 10);
            
            // Créer le bouton d'inscription seulement si des places sont disponibles
            const inscriptionBtn = placesDisponibles > 0 ? 
                (data.disable_native_inscription === true && data.external_inscription_url ? 
                    `<a href="${data.external_inscription_url}" class="go-booking-inscription-btn main-card-btn" target="_blank">
                        <span>${data.external_inscription_label || 'S\'inscrire'}</span>
                    </a>` :
                    `<button type="button" class="go-booking-inscription-btn main-card-btn" data-toggle="modal" data-event-id="${this.escapeHtml(data.id || '')}">
                        <span>S'inscrire</span>
                    </button>`) : '';
            
            // Vérifier et formater les données
            const templateData = {
                time: data.time || '',
                categories: this.getCategoriesTemplate(data.categories),
                title: this.escapeHtml(data.title || ''),
                lieu_depart: this.escapeHtml(data.lieu_depart || ''),
                places_disponibles: placesDisponibles,
                showInscriptionBtn: inscriptionBtn,
                id: this.escapeHtml(data.id || ''),
                disable_native_inscription: data.disable_native_inscription === true,
                external_inscription_url: data.external_inscription_url || '',
                external_inscription_label: data.external_inscription_label || ''
            };
            
            // Remplacer les variables dans le template
            const rendered = TEMPLATE.replace(/\${(\w+)}/g, (match, key) => {
                return templateData[key] !== undefined ? templateData[key] : '';
            });

            return rendered;
        } catch (error) {
            return null;
        }
    }

    // Nouvelle méthode pour générer le HTML des catégories
    getCategoriesTemplate(categories) {
        if (!Array.isArray(categories) || categories.length === 0) {
            return '';
        }

        // Utiliser le cache local pour les URLs d'icônes
        const iconsCache = this._categoryIconsCache || {};

        const html = categories.map(cat => {
            const categoryName = typeof cat === 'object' && cat.name ? cat.name : cat;
            let iconHtml = '';
            
            // Priorité à l'icône de l'objet catégorie si elle existe
            const iconUrl = typeof cat === 'object' && cat.icon ? cat.icon : iconsCache[categoryName];
            
            if (iconUrl) {
                iconHtml = `<img src="${this.getIconUrl(iconUrl)}" class="category-icon" alt="icône ${this.escapeHtml(categoryName)}" style="height:32px;display:inline-block;vertical-align:middle;margin-right:5px;border:1px solid #ccc;" />`;
            }
            
            // Traiter le cas où les catégories sont des objets avec name et color
            if (typeof cat === 'object' && cat.name) {
                // IMPORTANT: Utiliser DIRECTEMENT cat.color et assurons-nous qu'elle est valide
                let color;
                // Condition simplifiée et plus robuste
                if (cat.color && typeof cat.color === 'string' && cat.color.trim() !== '') {
                    color = cat.color;
                } else {
                    color = this.getCategoryColor(cat.name);
                }
                // Générer le HTML avec la couleur ET l'icône
                const html = `<span class="event-category-tag category-${this.escapeHtml(cat.name).replace(/\s+/g, '-').toLowerCase()}" 
                                data-category="${this.escapeHtml(cat.name)}">${iconHtml}${this.escapeHtml(cat.name)}</span>`;
                return html;
            }
            // Cas simple où les catégories sont des chaînes de caractères
            const cssClass = `category-${this.escapeHtml(cat).replace(/\s+/g, '-').toLowerCase()}`;
            return `<span class="event-category-tag ${cssClass}" data-category="${this.escapeHtml(cat)}">${iconHtml}${this.escapeHtml(cat)}</span>`;
        }).join('');

        return html;
    }

    // Méthode pour obtenir la couleur d'une catégorie - maintenant statique pour être utilisable globalement
    static getCategoryColor(category, event = null) {
        // Si la catégorie est un objet et a une propriété 'color'
        if (typeof category === 'object' && category.color) {
            return category.color;
        }
        
        // Si l'événement est fourni et a des catégories avec des couleurs définies
        if (event && event.extendedProps && event.extendedProps.categories) {
            const categories = event.extendedProps.categories;
            // Chercher si cette catégorie a une couleur définie
            for (let i = 0; i < categories.length; i++) {
                if (typeof categories[i] === 'object' && 
                    ((categories[i].name === category) || (categories[i].name === category.name)) && 
                    categories[i].color) {
                    return categories[i].color;
                }
            }
        }
        
        // Sinon, générer une couleur basée sur le nom de la catégorie
        const categoryName = typeof category === 'object' ? category.name : category;
        let hash = 0;
        
        // Calcul simple de hash pour le nom de la catégorie
        for (let i = 0; i < categoryName.length; i++) {
            hash = categoryName.charCodeAt(i) + ((hash << 5) - hash);
        }
        
        // Générer une couleur HSL avec saturation et luminosité fixes
        const h = Math.abs(hash) % 360;
        return `hsl(${h}, 70%, 50%)`;
    }

    // Ancienne méthode maintenue pour compatibilité, appelle maintenant la méthode statique
    getCategoryColor(category) {
        return AnimationCard.getCategoryColor(category, this.data);
    }

    // Méthode utilitaire pour échapper les caractères HTML
    escapeHtml(unsafe) {
        if (typeof unsafe !== 'string') {
            return unsafe;
        }
        return unsafe
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;")
            .replace(/'/g, "&#039;");
    }

    attachEventHandlers() {
        if (!this.element) {
            return;
        }

        const attachHandlers = () => {
            // Conservez uniquement les gestionnaires pour d'autres éléments comme le chevron
            const chevronIcon = this.element.querySelector('.chevron-icon');
            if (chevronIcon) {
                chevronIcon.removeEventListener('click', this.handleChevronClick.bind(this));
                chevronIcon.addEventListener('click', this.handleChevronClick.bind(this));
            }
        };

        // Si l'élément est déjà dans le DOM, attacher les gestionnaires immédiatement
        if (document.body.contains(this.element)) {
            attachHandlers();
            return;
        }

        // Sinon, utiliser MutationObserver pour détecter quand l'élément est ajouté au DOM
        const observer = new MutationObserver((mutations, obs) => {
            if (document.body.contains(this.element)) {
                obs.disconnect();
                attachHandlers();
            }
        });

        observer.observe(document.body, {
            childList: true,
            subtree: true
        });

        // Timeout de sécurité pour éviter une observation infinie
        setTimeout(() => {
            observer.disconnect();
        }, 10000);
    }

    // Gestionnaire d'événement pour l'icône chevron
    handleChevronClick(e) {
        e.preventDefault();
        e.stopPropagation();
        this.toggleDetails();
    }

    async toggleDetails() {
        const detailsContainer = this.element.querySelector('.fc-flexbox-event-details');
        const chevronIcon = this.element.querySelector('.chevron-icon');

        if (detailsContainer) {
            // Si les détails existent déjà, basculer leur visibilité
            const isVisible = detailsContainer.classList.contains('show');
            detailsContainer.classList.toggle('show');
            chevronIcon?.classList.toggle('rotate');
        } else {
            // Créer et ajouter les détails
            const details = document.createElement('div');
            details.className = 'fc-flexbox-event-details';
            details.innerHTML = TemplateService.eventDetails(this.data);
            
            this.element.querySelector('.event-flex-container').insertAdjacentElement('afterend', details);
            
            // Afficher les détails avec une animation
            requestAnimationFrame(() => {
                details.classList.add('show');
                chevronIcon?.classList.add('rotate');
            });

            // Initialiser la carte si des coordonnées sont disponibles
            if (this.data.extendedProps.coordinates) {
                await this.initializeMap(this.data.id, this.data.extendedProps.coordinates);
            }

        }
    }

    async initializeMap(eventId, coordinates) {
        const mapContainer = document.getElementById(`map-${eventId}`);
        if (!mapContainer || !coordinates) return;

        try {
            // Attendre que Leaflet soit chargé
            await new Promise(resolve => {
                if (window.L) resolve();
                else {
                    const checkLeaflet = setInterval(() => {
                        if (window.L) {
                            clearInterval(checkLeaflet);
                            resolve();
                        }
                    }, 100);
                }
            });

            const map = L.map(mapContainer).setView([coordinates.lat, coordinates.lng], 13);
            L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
                attribution: '© OpenStreetMap contributors'
            }).addTo(map);

            L.marker([coordinates.lat, coordinates.lng])
                .addTo(map)
                .bindPopup(this.data.title);

        } catch (error) {
            mapContainer.innerHTML = '<p>Erreur lors du chargement de la carte</p>';
        }
    }

    // Méthode pour faire défiler la page vers cette carte d'animation
    scrollIntoView(options = {}) {
        if (!this.element) return;
        
        // Valeurs par défaut pour les options
        const defaultOptions = {
            behavior: 'smooth',
            block: 'center'
        };
        
        const scrollOptions = { ...defaultOptions, ...options };
        
        // Utiliser la méthode scrollIntoView standard
        this.element.scrollIntoView(scrollOptions);
        
        // Pour certains navigateurs qui ne supportent pas block: 'center'
        if (scrollOptions.block === 'center' && !('scrollBehavior' in document.documentElement.style)) {
            const rect = this.element.getBoundingClientRect();
            const elementTop = rect.top + window.pageYOffset;
            const elementHeight = rect.height;
            const windowHeight = window.innerHeight;
            
            const centerPosition = elementTop - (windowHeight / 2) + (elementHeight / 2);
            
            window.scrollTo({
                top: centerPosition,
                behavior: scrollOptions.behavior
            });
        }
    }

    // Méthode pour ouvrir la modale d'inscription
    openInscriptionModal() {
        if (!this.data || !this.data.id) return Promise.reject('Données d\'animation invalides');
        
        // Pour des raisons de compatibilité, nous utilisons le format d'événement attendu par openInscriptionModal
        const eventFormat = {
            id: this.data.id,
            title: this.data.title,
            extendedProps: this.data.extendedProps || {}
        };
        
        // Chercher la fonction globale d'ouverture de modal
        if (typeof window.openInscriptionModal === 'function') {
            return Promise.resolve(window.openInscriptionModal(eventFormat));
        } else if (typeof openInscriptionModal === 'function') {
            return Promise.resolve(openInscriptionModal(eventFormat));
        } else {
            console.error('Fonction openInscriptionModal non disponible');
            return Promise.reject('Fonction d\'ouverture de modale non disponible');
        }
    }

    ensureCategoryStyles() {
        // Créer un élément style s'il n'existe pas déjà
        let styleElem = document.getElementById('go-booking-category-styles');
        if (!styleElem) {
            styleElem = document.createElement('style');
            styleElem.id = 'go-booking-category-styles';
            document.head.appendChild(styleElem);
        }
        
        // Style de base pour les icônes
        let iconStyles = `
        .event-category-tag .category-icon {
            height: 32px !important; /* Double la taille pour tester */
            border: none !important; /* Ajoute une bordure visible */
        }
        `;
        
        // Récupérer toutes les catégories avec leurs couleurs
        let categoryStyles = '';
        if (this.data.extendedProps && this.data.extendedProps.categories_colors) {
            for (const [cat, color] of Object.entries(this.data.extendedProps.categories_colors)) {
                if (color) {
                    categoryStyles += `
                    .category-${cat.replace(/\s+/g, '-').toLowerCase()} {
                        background-color: ${color} !important;
                    }`;
                }
            }
            styleElem.textContent = iconStyles + categoryStyles;
        }
    }

    // Méthode pour obtenir l'URL d'une icône
    getIconUrl(iconUrl) {
        if (typeof iconUrl === 'string' && iconUrl.trim() !== '') {
            // Vérifier si l'URL semble valide (commence par http ou /)
            if (iconUrl.startsWith('http') || iconUrl.startsWith('/')) {
                return iconUrl;
            }
        }
        // URL de secours si l'URL fournie n'est pas valide
        if (window.goBookingAjax && window.goBookingAjax.plugin_url) {
            return `${window.goBookingAjax.plugin_url}assets/images/default-icon.png`;
        }
        return '';
    }
}

// Fonction de compatibilité pour l'ancien code
function fillTemplate(data) {
    const card = new AnimationCard(data);
    return card.render();
}

// Rendre la classe et sa méthode accessibles globalement
window.AnimationCard = AnimationCard;
// Exposer la méthode de couleur pour être utilisée par calendar.js
window.getGlobalCategoryColor = AnimationCard.getCategoryColor;

export { AnimationCard, TEMPLATE, fillTemplate }; 
